/* ============================================
   ROOM DESIGNER — Math & Geometry Utilities
   Asset file: custom-room-designer-math.js

   Pure geometry functions with no instance state.
   Loaded before custom-room-designer.js via <script defer>.
   The main RoomDesigner class keeps thin wrappers
   that delegate here so existing call sites unchanged.

   SECTION MAP
   ───────────────────────────────────────────
   L16-56      Distance & proximity (getDistance, getWallParam, paramToPoint, distanceToWall, closestPointOnWall)
   L58-105     Room topology (getOpenEndpoints, mergeCollinearWalls)
   L107-364    Polygon operations (tracePolygon, buildRoomPolygon, pointInPolygon, segment intersect/clip)
   L366-409    Connected components + coincident wall removal
   L411-449    Rotation & arc utilities (_rotatePoint, _angleInArc, _arcAABB)
   L461+       Zone detection — planar subdivision (computeRoomZones)
   ============================================ */

window.RoomDesignerMath = (() => {
  const M = {};

  // ── Distance & Proximity ──────────────────

  M.getDistance = function(p1, p2) {
    const dx = p2.x - p1.x; const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  M.getWallParam = function(point, wall) {
    const dx = wall.end.x - wall.start.x; const dy = wall.end.y - wall.start.y;
    const lenSq = dx * dx + dy * dy; if (lenSq === 0) return 0;
    return Math.max(0, Math.min(1, ((point.x - wall.start.x) * dx + (point.y - wall.start.y) * dy) / lenSq));
  };

  M.paramToPoint = function(wall, param) {
    return { x: wall.start.x + param * (wall.end.x - wall.start.x), y: wall.start.y + param * (wall.end.y - wall.start.y) };
  };

  M.getWallLength = function(wall) {
    return M.getDistance(wall.start, wall.end);
  };

  M.distanceToWall = function(point, wall) {
    const A = point.x - wall.start.x; const B = point.y - wall.start.y;
    const C = wall.end.x - wall.start.x; const D = wall.end.y - wall.start.y;
    const dot = A * C + B * D; const lenSq = C * C + D * D;
    let param = lenSq !== 0 ? dot / lenSq : -1;
    let xx, yy;
    if (param < 0) { xx = wall.start.x; yy = wall.start.y; }
    else if (param > 1) { xx = wall.end.x; yy = wall.end.y; }
    else { xx = wall.start.x + param * C; yy = wall.start.y + param * D; }
    return Math.sqrt((point.x - xx) ** 2 + (point.y - yy) ** 2);
  };

  M.closestPointOnWall = function(point, wall) {
    const A = point.x - wall.start.x;
    const B = point.y - wall.start.y;
    const C = wall.end.x - wall.start.x;
    const D = wall.end.y - wall.start.y;
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    const param = Math.max(0, Math.min(1, dot / lenSq));
    return { x: wall.start.x + param * C, y: wall.start.y + param * D };
  };

  // ── Room Topology ─────────────────────────

  M.getOpenEndpoints = function(room) {
    if (!room || room.walls.length === 0) return [];
    const pointCount = {};
    const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    room.walls.forEach(wall => {
      const sk = key(wall.start); const ek = key(wall.end);
      pointCount[sk] = (pointCount[sk] || { point: wall.start, count: 0 }); pointCount[sk].count++;
      pointCount[ek] = (pointCount[ek] || { point: wall.end, count: 0 }); pointCount[ek].count++;
    });
    const endpoints = [];
    for (const k in pointCount) { if (pointCount[k].count === 1) endpoints.push(pointCount[k].point); }
    return endpoints;
  };

  M.mergeCollinearWalls = function(room) {
    if (!room || room.walls.length < 2) return;
    const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const getAngle = (wall) => Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
    const normalizeAngle = (a) => { while (a < 0) a += Math.PI; while (a >= Math.PI) a -= Math.PI; return a; };
    const anglesMatch = (a1, a2) => Math.abs(normalizeAngle(a1) - normalizeAngle(a2)) < 0.001;
    let merged = true;
    while (merged) {
      merged = false;
      for (let i = 0; i < room.walls.length; i++) {
        for (let j = i + 1; j < room.walls.length; j++) {
          const w1 = room.walls[i]; const w2 = room.walls[j];
          // CUSTOM_ROOMDESIGNER_BY_CLAUDE — curve facets must never be merged.
          // A curve is N near-collinear straight facets sharing a curveId; merging any
          // two of them silently flattens the curve AND drops the curve metadata (the
          // newWall literals below carry only start/end). This one guard protects all
          // ~12 mergeCollinearWalls call sites across math/render/wallseg/main.
          if (w1.curveId || w2.curveId) continue;
          // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
          if (!anglesMatch(getAngle(w1), getAngle(w2))) continue;
          const w1sk = key(w1.start), w1ek = key(w1.end), w2sk = key(w2.start), w2ek = key(w2.end);
          let newWall = null;
          if (w1ek === w2sk) newWall = { start: { x: w1.start.x, y: w1.start.y }, end: { x: w2.end.x, y: w2.end.y } };
          else if (w1ek === w2ek) newWall = { start: { x: w1.start.x, y: w1.start.y }, end: { x: w2.start.x, y: w2.start.y } };
          else if (w1sk === w2ek) newWall = { start: { x: w2.start.x, y: w2.start.y }, end: { x: w1.end.x, y: w1.end.y } };
          else if (w1sk === w2sk) newWall = { start: { x: w1.end.x, y: w1.end.y }, end: { x: w2.end.x, y: w2.end.y } };
          if (newWall) {
            // Skip merges that would produce a zero-length wall (e.g. reversed duplicates)
            const mdx = newWall.end.x - newWall.start.x, mdy = newWall.end.y - newWall.start.y;
            if (mdx * mdx + mdy * mdy < 0.0001) { continue; }
            room.walls[i] = newWall; room.walls.splice(j, 1); merged = true; break;
          }
        }
        if (merged) break;
      }
    }
    // Safety net: remove any zero-length walls created by merging
    room.walls = room.walls.filter(w => { const dx = w.end.x - w.start.x, dy = w.end.y - w.start.y; return dx * dx + dy * dy > 0.0001; });
  };

  // ── Polygon Operations ────────────────────

  M.tracePolygon = function(walls) {
    const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
    const startAdj = {}; const endAdj = {};
    walls.forEach((wall, idx) => {
      const sk = key(wall.start); const ek = key(wall.end);
      if (!startAdj[sk]) startAdj[sk] = []; if (!endAdj[ek]) endAdj[ek] = [];
      startAdj[sk].push({ wall, idx }); endAdj[ek].push({ wall, idx });
    });
    const polygon = []; const visited = new Set();
    let currentPoint = walls[0].start;
    polygon.push({ x: currentPoint.x, y: currentPoint.y });
    visited.add(0); currentPoint = walls[0].end;
    polygon.push({ x: currentPoint.x, y: currentPoint.y });
    for (let step = 0; step < walls.length; step++) {
      const ck = key(currentPoint); let found = false;
      const fromStart = startAdj[ck] || [];
      for (const entry of fromStart) { if (!visited.has(entry.idx)) { visited.add(entry.idx); currentPoint = entry.wall.end; polygon.push({ x: currentPoint.x, y: currentPoint.y }); found = true; break; } }
      if (!found) { const fromEnd = endAdj[ck] || []; for (const entry of fromEnd) { if (!visited.has(entry.idx)) { visited.add(entry.idx); currentPoint = entry.wall.start; polygon.push({ x: currentPoint.x, y: currentPoint.y }); found = true; break; } } }
      if (!found) break;
    }
    return polygon.length >= 3 ? polygon : null;
  };

  M.buildRoomPolygon = function(room, closingStart, closingEnd) {
    const walls = closingStart && closingEnd ? [...room.walls, { start: closingStart, end: closingEnd }] : [...room.walls];
    if (walls.length < 3) return null;
    return M.tracePolygon(walls);
  };

  M.pointInPolygon = function(px, py, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y, xj = polygon[j].x, yj = polygon[j].y;
      if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };

  // ── Segment Intersection & Clipping ───────

  M._segmentsIntersect = function(s1, s2) {
    const d1x = s1.end.x - s1.start.x, d1y = s1.end.y - s1.start.y;
    const d2x = s2.end.x - s2.start.x, d2y = s2.end.y - s2.start.y;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-10) return false;
    const t = ((s2.start.x - s1.start.x) * d2y - (s2.start.y - s1.start.y) * d2x) / denom;
    const u = ((s2.start.x - s1.start.x) * d1y - (s2.start.y - s1.start.y) * d1x) / denom;
    return t > 0.001 && t < 0.999 && u > 0.001 && u < 0.999;
  };

  M._segmentsCollinearOverlap = function(s1, s2, eps) {
    const dx = s2.end.x - s2.start.x, dy = s2.end.y - s2.start.y;
    const len = Math.sqrt(dx * dx + dy * dy); if (len < eps) return false;
    const cross1 = Math.abs((s1.start.x - s2.start.x) * dy - (s1.start.y - s2.start.y) * dx) / len;
    const cross2 = Math.abs((s1.end.x - s2.start.x) * dy - (s1.end.y - s2.start.y) * dx) / len;
    if (cross1 > eps || cross2 > eps) return false;
    const t1 = ((s1.start.x - s2.start.x) * dx + (s1.start.y - s2.start.y) * dy) / (len * len);
    const t2 = ((s1.end.x - s2.start.x) * dx + (s1.end.y - s2.start.y) * dy) / (len * len);
    return (Math.min(1, Math.max(t1, t2)) - Math.max(0, Math.min(t1, t2))) > eps / len;
  };

  M._subtractOverlappingSegments = function(wall, cutoutWalls, eps) {
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const len = Math.sqrt(dx * dx + dy * dy); if (len < eps) return [];
    const overlaps = [];
    for (const cWall of cutoutWalls) {
      const cdx = cWall.end.x - cWall.start.x, cdy = cWall.end.y - cWall.start.y;
      if (Math.sqrt(cdx * cdx + cdy * cdy) < eps) continue;
      const cross1 = Math.abs((cWall.start.x - wall.start.x) * dy - (cWall.start.y - wall.start.y) * dx) / len;
      const cross2 = Math.abs((cWall.end.x - wall.start.x) * dy - (cWall.end.y - wall.start.y) * dx) / len;
      if (cross1 > eps || cross2 > eps) continue;
      const t1 = ((cWall.start.x - wall.start.x) * dx + (cWall.start.y - wall.start.y) * dy) / (len * len);
      const t2 = ((cWall.end.x - wall.start.x) * dx + (cWall.end.y - wall.start.y) * dy) / (len * len);
      const tMin = Math.max(0, Math.min(t1, t2)); const tMax = Math.min(1, Math.max(t1, t2));
      if (tMax - tMin > eps / len) overlaps.push([tMin, tMax]);
    }
    if (overlaps.length === 0) return [wall];
    overlaps.sort((a, b) => a[0] - b[0]);
    const merged = [overlaps[0]];
    for (let i = 1; i < overlaps.length; i++) { const last = merged[merged.length - 1]; if (overlaps[i][0] <= last[1] + eps / len) last[1] = Math.max(last[1], overlaps[i][1]); else merged.push(overlaps[i]); }
    const segments = []; let t = 0;
    for (const [tStart, tEnd] of merged) {
      if (tStart - t > eps / len) segments.push({ start: { x: wall.start.x + t * dx, y: wall.start.y + t * dy }, end: { x: wall.start.x + tStart * dx, y: wall.start.y + tStart * dy } });
      t = tEnd;
    }
    if (1 - t > eps / len) segments.push({ start: { x: wall.start.x + t * dx, y: wall.start.y + t * dy }, end: { x: wall.end.x, y: wall.end.y } });
    return segments;
  };

  M._subtractInsideRoom = function(wall, boundaryWalls, eps) {
    const dx = wall.end.x - wall.start.x;
    const dy = wall.end.y - wall.start.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < eps) return [];
    const nx = -dy / len;
    const ny = dx / len;

    // Find intersections with all boundary walls
    const intersections = [];
    for (const bw of boundaryWalls) {
      const bx = bw.end.x - bw.start.x;
      const by = bw.end.y - bw.start.y;
      const denom = dx * by - dy * bx;
      if (Math.abs(denom) < 1e-10) continue;
      const t = ((bw.start.x - wall.start.x) * by - (bw.start.y - wall.start.y) * bx) / denom;
      const u = ((bw.start.x - wall.start.x) * dy - (bw.start.y - wall.start.y) * dx) / denom;
      if (t > eps / len && t < 1 - eps / len && u >= -0.001 && u <= 1.001) intersections.push(t);
    }

    // Inject split points from collinear boundary wall endpoints
    for (const bw of boundaryWalls) {
      const bdx = bw.end.x - bw.start.x;
      const bdy = bw.end.y - bw.start.y;
      const bLen = Math.sqrt(bdx * bdx + bdy * bdy);
      if (bLen < eps) continue;
      const cross1 = Math.abs((wall.start.x - bw.start.x) * bdy - (wall.start.y - bw.start.y) * bdx) / bLen;
      const cross2 = Math.abs((wall.end.x - bw.start.x) * bdy - (wall.end.y - bw.start.y) * bdx) / bLen;
      if (cross1 < eps * 3 && cross2 < eps * 3) {
        const tBwStart = ((bw.start.x - wall.start.x) * dx + (bw.start.y - wall.start.y) * dy) / (len * len);
        const tBwEnd = ((bw.end.x - wall.start.x) * dx + (bw.end.y - wall.start.y) * dy) / (len * len);
        if (tBwStart > eps / len && tBwStart < 1 - eps / len) intersections.push(tBwStart);
        if (tBwEnd > eps / len && tBwEnd < 1 - eps / len) intersections.push(tBwEnd);
      }
    }

    intersections.sort((a, b) => a - b);
    const dedupedT = [0];
    for (const t of intersections) {
      if (t - dedupedT[dedupedT.length - 1] > eps / len) dedupedT.push(t);
    }
    dedupedT.push(1);
    const allT = dedupedT;
    const result = [];

    // Helper: even-odd ray cast
    const rayCast = (px, py) => {
      let crossings = 0;
      for (const bw of boundaryWalls) {
        const y1 = bw.start.y, y2 = bw.end.y;
        const x1 = bw.start.x, x2 = bw.end.x;
        if ((y1 > py) !== (y2 > py)) {
          const xInt = x1 + (py - y1) * (x2 - x1) / (y2 - y1);
          if (px < xInt) crossings++;
        }
      }
      return crossings % 2 === 1;
    };

    for (let i = 0; i < allT.length - 1; i++) {
      const tStart = allT[i];
      const tEnd = allT[i + 1];
      if (tEnd - tStart < eps / len) continue;
      const tMid = (tStart + tEnd) / 2;
      const midX = wall.start.x + tMid * dx;
      const midY = wall.start.y + tMid * dy;
      const seg = {
        start: { x: wall.start.x + tStart * dx, y: wall.start.y + tStart * dy },
        end: { x: wall.start.x + tEnd * dx, y: wall.start.y + tEnd * dy }
      };

      // Check if segment is collinear with any boundary wall
      let isCollinear = false;
      for (const bw of boundaryWalls) {
        const bdx = bw.end.x - bw.start.x;
        const bdy = bw.end.y - bw.start.y;
        const bLen = Math.sqrt(bdx * bdx + bdy * bdy);
        if (bLen < eps) continue;
        if (Math.abs((midX - bw.start.x) * bdy - (midY - bw.start.y) * bdx) / bLen < eps * 3) {
          isCollinear = true;
          break;
        }
      }

      if (isCollinear) {
        // Ray casting from a point ON a boundary line is unreliable.
        // Instead, test points slightly offset perpendicular to the wall.
        const offsetDist = eps * 10;
        const side1Inside = rayCast(midX + nx * offsetDist, midY + ny * offsetDist);
        const side2Inside = rayCast(midX - nx * offsetDist, midY - ny * offsetDist);

        if (side1Inside && side2Inside) {
          // Both sides are filled → segment is interior → remove
          continue;
        }
        // One or both sides are empty → segment is on boundary or outside → keep
        result.push(seg);
      } else {
        // Standard ray cast from midpoint
        if (!rayCast(midX, midY)) result.push(seg);
      }
    }
    return result;
  };

  M._subtractInsidePolygon = function(wall, polygon, eps) {
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const len = Math.sqrt(dx * dx + dy * dy); if (len < eps) return [];
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length; const edge = { start: polygon[i], end: polygon[j] };
      const edx = edge.end.x - edge.start.x, edy = edge.end.y - edge.start.y;
      const eLen = Math.sqrt(edx * edx + edy * edy); if (eLen < eps) continue;
      const cross1 = Math.abs((wall.start.x - edge.start.x) * edy - (wall.start.y - edge.start.y) * edx) / eLen;
      const cross2 = Math.abs((wall.end.x - edge.start.x) * edy - (wall.end.y - edge.start.y) * edx) / eLen;
      if (cross1 < eps * 3 && cross2 < eps * 3) return [wall];
    }
    const intersections = [];
    for (let i = 0; i < polygon.length; i++) {
      const j = (i + 1) % polygon.length;
      const ex = polygon[j].x - polygon[i].x, ey = polygon[j].y - polygon[i].y;
      const denom = dx * ey - dy * ex; if (Math.abs(denom) < 1e-10) continue;
      const t = ((polygon[i].x - wall.start.x) * ey - (polygon[i].y - wall.start.y) * ex) / denom;
      const u = ((polygon[i].x - wall.start.x) * dy - (polygon[i].y - wall.start.y) * dx) / denom;
      if (t > eps / len && t < 1 - eps / len && u >= -0.001 && u <= 1.001) intersections.push(t);
    }
    intersections.sort((a, b) => a - b);
    const allT = [0, ...intersections, 1]; const result = [];
    for (let i = 0; i < allT.length - 1; i++) {
      const tStart = allT[i], tEnd = allT[i + 1]; if (tEnd - tStart < eps / len) continue;
      const tMid = (tStart + tEnd) / 2;
      const midX = wall.start.x + tMid * dx, midY = wall.start.y + tMid * dy;
      let onBoundary = false;
      for (let pi = 0; pi < polygon.length; pi++) {
        const pj = (pi + 1) % polygon.length; const edge = { start: polygon[pi], end: polygon[pj] };
        const edx = edge.end.x - edge.start.x, edy = edge.end.y - edge.start.y;
        const eLen = Math.sqrt(edx * edx + edy * edy); if (eLen < eps) continue;
        if (Math.abs((midX - edge.start.x) * edy - (midY - edge.start.y) * edx) / eLen < eps * 3) { onBoundary = true; break; }
      }
      if (onBoundary || !M.pointInPolygon(midX, midY, polygon)) {
        result.push({ start: { x: wall.start.x + tStart * dx, y: wall.start.y + tStart * dy }, end: { x: wall.start.x + tEnd * dx, y: wall.start.y + tEnd * dy } });
      }
    }
    return result;
  };

  M._clipWallToRoomPoly = function(wall, roomPoly) {
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const intersections = [];
    for (let i = 0; i < roomPoly.length; i++) {
      const j = (i + 1) % roomPoly.length;
      const ex = roomPoly[j].x - roomPoly[i].x, ey = roomPoly[j].y - roomPoly[i].y;
      const denom = dx * ey - dy * ex; if (Math.abs(denom) < 1e-10) continue;
      const t = ((roomPoly[i].x - wall.start.x) * ey - (roomPoly[i].y - wall.start.y) * ex) / denom;
      const u = ((roomPoly[i].x - wall.start.x) * dy - (roomPoly[i].y - wall.start.y) * dx) / denom;
      if (t > 0.001 && t < 0.999 && u >= -0.001 && u <= 1.001) intersections.push(t);
    }
    intersections.sort((a, b) => a - b);
    const allT = [0, ...intersections, 1]; const result = [];
    for (let i = 0; i < allT.length - 1; i++) {
      const tStart = allT[i], tEnd = allT[i + 1]; if (tEnd - tStart < 0.001) continue;
      const tMid = (tStart + tEnd) / 2;
      if (M.pointInPolygon(wall.start.x + tMid * dx, wall.start.y + tMid * dy, roomPoly)) {
        result.push({ start: { x: wall.start.x + tStart * dx, y: wall.start.y + tStart * dy }, end: { x: wall.start.x + tEnd * dx, y: wall.start.y + tEnd * dy } });
      }
    }
    return result;
  };

  // ── Connected Components ──────────────────

  M._getConnectedComponents = function(walls) {
    if (walls.length === 0) return [];
    const EPS = 0.05;
    const areClose = (p1, p2) => Math.abs(p1.x - p2.x) < EPS && Math.abs(p1.y - p2.y) < EPS;
    const sharesEndpoint = (w1, w2) => areClose(w1.start, w2.start) || areClose(w1.start, w2.end) || areClose(w1.end, w2.start) || areClose(w1.end, w2.end);
    const visited = new Array(walls.length).fill(false); const components = [];
    for (let i = 0; i < walls.length; i++) {
      if (visited[i]) continue;
      const component = []; const queue = [i]; visited[i] = true;
      while (queue.length > 0) {
        const idx = queue.shift(); component.push(walls[idx]);
        for (let j = 0; j < walls.length; j++) { if (visited[j]) continue; if (sharesEndpoint(walls[idx], walls[j])) { visited[j] = true; queue.push(j); } }
      }
      components.push(component);
    }
    return components;
  };

  M._removeCoincidentWalls = function(walls, eps) {
    // Pass 1: Remove full-segment duplicates.
    // Same-direction duplicates: keep one copy.
    // Opposite-direction duplicates: remove both (they cancel out).
    const toRemove = new Set();
    for (let i = 0; i < walls.length; i++) {
      if (toRemove.has(i)) continue;
      for (let j = i + 1; j < walls.length; j++) {
        if (toRemove.has(j)) continue;
        const w1 = walls[i], w2 = walls[j];
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — never treat a curve facet as a duplicate.
        // This routine exists to cancel coincident CUTOUT edges; a curve facet is
        // authored geometry. Two curves bowing toward each other bring facets within
        // eps (0.02ft ~ a quarter inch) of each other, and because the two curves run
        // opposite ways around the room they land in the revDir branch below, which
        // deletes BOTH — walls vanishing out of the drawing. Same reasoning as the
        // guard in mergeCollinearWalls.
        if (w1.curveId || w2.curveId) continue;
        // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
        const close = (a, b) => Math.abs(a.x - b.x) < eps && Math.abs(a.y - b.y) < eps;
        const sameDir = close(w1.start, w2.start) && close(w1.end, w2.end);
        const revDir  = close(w1.start, w2.end) && close(w1.end, w2.start);
        if (sameDir) {
          toRemove.add(j);  // keep i, remove duplicate j
        } else if (revDir) {
          toRemove.add(i);  // opposite directions cancel — remove both
          toRemove.add(j);
          break;
        }
      }
    }
    return walls.filter((_, idx) => !toRemove.has(idx));
  };

  // ── Rotation & Arc Utilities ──────────────

  M._rotatePoint = function(px, py, cx, cy, angle) {
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const dx = px - cx, dy = py - cy;
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
  };

  M._angleInArc = function(angle, start, end, ccw) {
    const TWO_PI = Math.PI * 2;
    const norm = a => ((a % TWO_PI) + TWO_PI) % TWO_PI;
    const a = norm(angle), s = norm(start), e = norm(end);
    if (ccw) {                       // CCW: arc goes from s downward to e
      return s >= e ? (a <= s && a >= e) : (a <= s || a >= e);
    } else {                         // CW: arc goes from s upward to e
      return s <= e ? (a >= s && a <= e) : (a >= s || a <= e);
    }
  };

  /** Compute axis-aligned bounding box of a circular arc. */
  M._arcAABB = function(cx, cy, radius, startAngle, endAngle, ccw) {
    const pts = [
      { x: cx + radius * Math.cos(startAngle), y: cy + radius * Math.sin(startAngle) },
      { x: cx + radius * Math.cos(endAngle),   y: cy + radius * Math.sin(endAngle) }
    ];
    // Check cardinal directions (0, π/2, π, 3π/2) for extremes
    const cardinals = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
    for (const c of cardinals) {
      if (M._angleInArc(c, startAngle, endAngle, ccw)) {
        pts.push({ x: cx + radius * Math.cos(c), y: cy + radius * Math.sin(c) });
      }
    }
    // Include the center (the pie-slice triangular area)
    pts.push({ x: cx, y: cy });
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pts) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); }
    const pad = 2 / 12; // 2 inches padding
    return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
  };

  // ── Zone Detection (Planar Subdivision) ──

  /** Segment-segment intersection returning parameters on both segments.
   *  Returns {t1, t2, point} or null if parallel/non-intersecting. */
  M.segSegIntersectionParams = function(s1, s2) {
    const d1x = s1.end.x - s1.start.x, d1y = s1.end.y - s1.start.y;
    const d2x = s2.end.x - s2.start.x, d2y = s2.end.y - s2.start.y;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-10) return null;
    const t1 = ((s2.start.x - s1.start.x) * d2y - (s2.start.y - s1.start.y) * d2x) / denom;
    const t2 = ((s2.start.x - s1.start.x) * d1y - (s2.start.y - s1.start.y) * d1x) / denom;
    if (t1 < 0.001 || t1 > 0.999 || t2 < 0.001 || t2 > 0.999) return null;
    return {
      t1, t2,
      point: { x: s1.start.x + t1 * d1x, y: s1.start.y + t1 * d1y }
    };
  };

  /** Signed area of a polygon (positive = CCW, negative = CW). */
  M.signedPolygonArea = function(polygon) {
    let area = 0;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      area += (polygon[j].x - polygon[i].x) * (polygon[j].y + polygon[i].y);
    }
    return area / 2;
  };

  /** Area-weighted centroid of a polygon (shoelace formula). */
  M.polygonCentroid = function(polygon) {
    const n = polygon.length;
    if (n === 0) return { x: 0, y: 0 };
    if (n <= 2) {
      let sx = 0, sy = 0;
      for (const p of polygon) { sx += p.x; sy += p.y; }
      return { x: sx / n, y: sy / n };
    }
    let area = 0, cx = 0, cy = 0;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const cross = polygon[j].x * polygon[i].y - polygon[i].x * polygon[j].y;
      area += cross;
      cx += (polygon[j].x + polygon[i].x) * cross;
      cy += (polygon[j].y + polygon[i].y) * cross;
    }
    area /= 2;
    if (Math.abs(area) < 1e-10) {
      // Degenerate polygon — fall back to vertex average
      let sx = 0, sy = 0;
      for (const p of polygon) { sx += p.x; sy += p.y; }
      return { x: sx / n, y: sy / n };
    }
    const f = 1 / (6 * area);
    return { x: cx * f, y: cy * f };
  };

  /** Interior point suitable for labelling a polygon that may have holes.
   *  The area centroid of an annulus (or a concave zone) can land outside the
   *  material; this scanlines the bounding box and keeps the interior candidate
   *  farthest from any edge. Falls back to the centroid if nothing is found.
   *  @param {Array} polygon — outer ring [{x,y}, ...]
   *  @param {Array} holes — inner rings to exclude
   *  @returns {{x:number, y:number}} */
  M.polygonVisualCenter = function(polygon, holes) {
    const centroid = M.polygonCentroid(polygon);
    if (!polygon || polygon.length < 3) return centroid;
    const rings = (holes || []).filter(h => h && h.length >= 3);
    if (rings.length === 0 && M.pointInPolygon(centroid.x, centroid.y, polygon)) return centroid;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of polygon) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    const width = maxX - minX, height = maxY - minY;
    if (width <= 0 || height <= 0) return centroid;

    const isInside = (x, y) => {
      if (!M.pointInPolygon(x, y, polygon)) return false;
      for (const h of rings) if (M.pointInPolygon(x, y, h)) return false;
      return true;
    };
    const edges = [];
    const addRing = (ring) => {
      for (let i = 0; i < ring.length; i++) {
        edges.push({ start: ring[i], end: ring[(i + 1) % ring.length] });
      }
    };
    addRing(polygon);
    for (const h of rings) addRing(h);
    const clearance = (x, y) => {
      let min = Infinity;
      for (const e of edges) {
        const d = M.distanceToWall({ x, y }, e);
        if (d < min) min = d;
      }
      return min;
    };

    // Scan rows, take the midpoint of each interior span, keep the roomiest one
    const ROWS = 24, COLS = 48;
    let best = null, bestClear = -1;
    for (let ri = 0; ri <= ROWS; ri++) {
      const y = minY + (ri / ROWS) * height;
      let spanStart = null;
      for (let ci = 0; ci <= COLS; ci++) {
        const x = minX + (ci / COLS) * width;
        const inside = isInside(x, y);
        if (inside && spanStart === null) spanStart = x;
        else if (!inside && spanStart !== null) {
          const mx = (spanStart + x) / 2;
          if (isInside(mx, y)) { const c = clearance(mx, y); if (c > bestClear) { bestClear = c; best = { x: mx, y }; } }
          spanStart = null;
        }
      }
      if (spanStart !== null) {
        const mx = (spanStart + maxX) / 2;
        if (isInside(mx, y)) { const c = clearance(mx, y); if (c > bestClear) { bestClear = c; best = { x: mx, y }; } }
      }
    }
    return best || centroid;
  };

  /** Compute sub-zone polygons by subdividing a room polygon with divider segments.
   *  @param {Array} roomPolygon — closed polygon [{x,y}, ...]
   *  @param {Array} dividerSegments — [{start:{x,y}, end:{x,y}}, ...]
   *  @param {Array} holePolygons — polygons to exclude (cutouts/holes)
   *  @returns {Array} [{polygon, centroid, area}, ...] — one per zone */
  M.computeRoomZones = function(roomPolygon, dividerSegments, holePolygons) {
    if (!roomPolygon || roomPolygon.length < 3) return [];
    // Single zone covering the whole room — used by every early-out below
    const wholeRoomZone = () => {
      const c = M.polygonCentroid(roomPolygon);
      return [{ polygon: roomPolygon, holes: [], centroid: c, labelPoint: c, area: Math.abs(M.signedPolygonArea(roomPolygon)) }];
    };
    if (!dividerSegments || dividerSegments.length === 0) {
      return wholeRoomZone();
    }

    const EPS = 0.05; // feet tolerance (~0.6 inches)
    const close = (a, b) => Math.abs(a.x - b.x) < EPS && Math.abs(a.y - b.y) < EPS;
    const vKey = (p) => `${(Math.round(p.x * 40) / 40).toFixed(3)},${(Math.round(p.y * 40) / 40).toFixed(3)}`;

    // --- Step 1: Collect all edges and find split points ---

    // Room boundary edges (directed, following winding)
    const boundaryEdges = [];
    for (let i = 0; i < roomPolygon.length; i++) {
      const j = (i + 1) % roomPolygon.length;
      boundaryEdges.push({ start: roomPolygon[i], end: roomPolygon[j] });
    }

    // Clip divider segments to room interior and filter degenerate ones
    const clippedDividers = [];
    for (const seg of dividerSegments) {
      const clipped = M._clipWallToRoomPoly(seg, roomPolygon);
      for (const c of clipped) {
        const len = M.getDistance(c.start, c.end);
        if (len > EPS) clippedDividers.push(c);
      }
    }
    // Snap clipped divider endpoints to exact boundary vertices to prevent DCEL merge failures
    for (const div of clippedDividers) {
      for (const key of ['start', 'end']) {
        for (const bv of roomPolygon) {
          if (close(div[key], bv)) {
            div[key] = { x: bv.x, y: bv.y };
            break;
          }
        }
      }
    }
    if (clippedDividers.length === 0) {
      return wholeRoomZone();
    }

    // Collect split-t values per boundary edge, and per divider
    const boundarySplits = boundaryEdges.map(() => []);
    const dividerSplits = clippedDividers.map(() => []);

    // Divider endpoints landing on boundary edges
    for (let di = 0; di < clippedDividers.length; di++) {
      const div = clippedDividers[di];
      for (const endPt of [div.start, div.end]) {
        for (let bi = 0; bi < boundaryEdges.length; bi++) {
          const bw = boundaryEdges[bi];
          const dist = M.distanceToWall(endPt, bw);
          if (dist < EPS) {
            const t = M.getWallParam(endPt, bw);
            if (t > 0.001 && t < 0.999) {
              boundarySplits[bi].push(t);
            }
          }
        }
      }
    }

    // Divider-boundary intersections
    for (let di = 0; di < clippedDividers.length; di++) {
      for (let bi = 0; bi < boundaryEdges.length; bi++) {
        const hit = M.segSegIntersectionParams(clippedDividers[di], boundaryEdges[bi]);
        if (hit) {
          dividerSplits[di].push(hit.t1);
          boundarySplits[bi].push(hit.t2);
        }
      }
    }

    // Divider-divider intersections
    for (let i = 0; i < clippedDividers.length; i++) {
      for (let j = i + 1; j < clippedDividers.length; j++) {
        const hit = M.segSegIntersectionParams(clippedDividers[i], clippedDividers[j]);
        if (hit) {
          dividerSplits[i].push(hit.t1);
          dividerSplits[j].push(hit.t2);
        }
      }
    }

    // Divider endpoint T-junctions on other dividers
    for (let i = 0; i < clippedDividers.length; i++) {
      const div = clippedDividers[i];
      for (const endPt of [div.start, div.end]) {
        for (let j = 0; j < clippedDividers.length; j++) {
          if (i === j) continue;
          const other = clippedDividers[j];
          const dist = M.distanceToWall(endPt, other);
          if (dist < EPS) {
            const t = M.getWallParam(endPt, other);
            if (t > 0.001 && t < 0.999) {
              dividerSplits[j].push(t);
            }
          }
        }
      }
    }

    // --- Step 2: Split edges into sub-edges ---
    const splitEdge = (edge, tVals) => {
      const filtered = [...new Set(tVals.map(t => Math.round(t * 10000) / 10000))]
        .filter(t => t > 0.001 && t < 0.999)
        .sort((a, b) => a - b);
      if (filtered.length === 0) return [edge];
      const result = [];
      let prev = 0;
      for (const t of filtered) {
        if (t - prev < 0.001) continue;
        result.push({
          start: M.paramToPoint(edge, prev),
          end: M.paramToPoint(edge, t)
        });
        prev = t;
      }
      if (1 - prev > 0.001) {
        result.push({ start: M.paramToPoint(edge, prev), end: { x: edge.end.x, y: edge.end.y } });
      }
      return result;
    };

    // All sub-edges (undirected initially, will become half-edges)
    const allEdges = [];
    for (let bi = 0; bi < boundaryEdges.length; bi++) {
      const subs = splitEdge(boundaryEdges[bi], boundarySplits[bi]);
      // Tag boundary sub-edges so the room's connected component can be identified
      // later (its outer cycle is the unbounded face; other components' outer
      // cycles are island rims = holes).
      for (const s of subs) { s._b = true; allEdges.push(s); }
    }
    for (let di = 0; di < clippedDividers.length; di++) {
      const subs = splitEdge(clippedDividers[di], dividerSplits[di]);
      for (const s of subs) allEdges.push(s);
    }

    // Filter out zero-length edges
    const validEdges = allEdges.filter(e => M.getDistance(e.start, e.end) > EPS * 0.5);

    // --- Step 3: Build half-edge graph ---
    // Merge vertices that are close together
    const vertices = []; // [{x,y}]
    const vertexMap = new Map(); // vKey → index

    const getVertexIdx = (p) => {
      const k = vKey(p);
      if (vertexMap.has(k)) return vertexMap.get(k);
      // Check nearby existing vertices (in case of floating point drift)
      for (let vi = 0; vi < vertices.length; vi++) {
        if (close(vertices[vi], p)) {
          vertexMap.set(k, vi);
          return vi;
        }
      }
      const idx = vertices.length;
      vertices.push({ x: p.x, y: p.y });
      vertexMap.set(k, idx);
      return idx;
    };

    // Build half-edges: each undirected edge → 2 directed half-edges
    const halfEdges = []; // [{from, to, twin, next, visited}]
    let boundaryVertexIdx = -1;
    for (const e of validEdges) {
      const fromIdx = getVertexIdx(e.start);
      const toIdx = getVertexIdx(e.end);
      if (fromIdx === toIdx) continue;
      if (e._b && boundaryVertexIdx === -1) boundaryVertexIdx = fromIdx;
      const he1 = { from: fromIdx, to: toIdx, twin: -1, next: -1, visited: false };
      const he2 = { from: toIdx, to: fromIdx, twin: -1, next: -1, visited: false };
      he1.twin = halfEdges.length + 1;
      he2.twin = halfEdges.length;
      halfEdges.push(he1);
      halfEdges.push(he2);
    }

    if (halfEdges.length === 0) {
      return wholeRoomZone();
    }

    // Connected components over the vertex graph. A wall-anchored divider shares
    // vertices with the room boundary (one component); a free-standing island loop
    // is its own component. Each component traces exactly one negative-area cycle:
    // for the boundary's component that's the unbounded face (discard), for an
    // island component it's the rim of a hole in whatever face surrounds it.
    const ufParent = new Array(vertices.length);
    for (let i = 0; i < vertices.length; i++) ufParent[i] = i;
    const ufFind = (a) => { while (ufParent[a] !== a) { ufParent[a] = ufParent[ufParent[a]]; a = ufParent[a]; } return a; };
    for (const he of halfEdges) {
      const ra = ufFind(he.from), rb = ufFind(he.to);
      if (ra !== rb) ufParent[ra] = rb;
    }
    const mainComp = boundaryVertexIdx >= 0 ? ufFind(boundaryVertexIdx) : -1;

    // Build adjacency: for each vertex, list outgoing half-edges sorted by angle
    const outgoing = new Array(vertices.length).fill(null).map(() => []);
    for (let hi = 0; hi < halfEdges.length; hi++) {
      outgoing[halfEdges[hi].from].push(hi);
    }

    // Sort outgoing edges by angle
    for (let vi = 0; vi < vertices.length; vi++) {
      const v = vertices[vi];
      outgoing[vi].sort((a, b) => {
        const va = vertices[halfEdges[a].to];
        const vb = vertices[halfEdges[b].to];
        const angA = Math.atan2(va.y - v.y, va.x - v.x);
        const angB = Math.atan2(vb.y - v.y, vb.x - v.x);
        return angA - angB;
      });
    }

    // --- Step 4: Link "next" pointers ---
    // For a half-edge arriving at vertex V (twin of some he), the next half-edge
    // leaving V is the one "just after" the arriving direction in CW order.
    // In the sorted outgoing list (CCW by angle), the "next" is the PREVIOUS entry.
    for (let hi = 0; hi < halfEdges.length; hi++) {
      const he = halfEdges[hi];
      const arrivalVertex = he.to;
      const twinIdx = he.twin;
      // The twin goes from arrivalVertex → he.from, so it's in outgoing[arrivalVertex]
      const outs = outgoing[arrivalVertex];
      if (outs.length === 0) continue;
      const twinPos = outs.indexOf(twinIdx);
      if (twinPos === -1) continue;
      // Next in CW traversal = previous in CCW-sorted list
      const nextPos = (twinPos - 1 + outs.length) % outs.length;
      he.next = outs[nextPos];
    }

    // --- Step 5: Trace faces ---
    const faces = [];
    for (let hi = 0; hi < halfEdges.length; hi++) {
      if (halfEdges[hi].visited) continue;
      if (halfEdges[hi].next === -1) { halfEdges[hi].visited = true; continue; }

      const faceEdges = [];
      let cur = hi;
      let safetyLimit = halfEdges.length + 2;
      while (!halfEdges[cur].visited && safetyLimit-- > 0) {
        halfEdges[cur].visited = true;
        faceEdges.push(cur);
        cur = halfEdges[cur].next;
        if (cur === -1) break;
        if (cur === hi) break;
      }

      if (faceEdges.length >= 3) {
        const poly = faceEdges.map(ei => {
          const v = vertices[halfEdges[ei].from];
          return { x: v.x, y: v.y };
        });
        faces.push({ poly, comp: ufFind(halfEdges[faceEdges[0]].from) });
      }
    }

    // --- Step 6: Filter faces, attach island rims as holes ---
    // DCEL face tracing with the "previous in CCW-sorted" rule always produces:
    //   - Inner zone faces with POSITIVE signed area (CW on screen)
    //   - One NEGATIVE-area cycle per connected component
    // This holds regardless of room polygon winding direction.
    // The boundary component's negative cycle is the unbounded face and is dropped.
    // Any other negative cycle is a free-standing island: it becomes a HOLE in the
    // smallest face that surrounds it, so the surrounding zone is a true annulus
    // (correct area, and flooring stops at the island edge).
    const positives = []; // {polygon, area, comp, holes[]}
    const rims = [];      // {polygon, area, comp}
    for (const f of faces) {
      const signedArea = M.signedPolygonArea(f.poly);
      if (signedArea >= 0.01) {
        positives.push({ polygon: f.poly, area: signedArea, comp: f.comp, holes: [] });
      } else if (signedArea <= -0.01 && f.comp !== mainComp) {
        rims.push({ polygon: f.poly, area: -signedArea, comp: f.comp });
      }
      // else: unbounded face, or degenerate sliver — dropped
    }

    // Attach each island rim to the smallest face that contains it. A rim vertex is
    // strictly interior to the surrounding face (disconnected components never touch),
    // so a plain point-in-polygon test resolves it — and picking the SMALLEST container
    // makes nested islands (island inside an island) fall out for free.
    for (const rim of rims) {
      const probe = rim.polygon[0];
      let best = null;
      for (const f of positives) {
        if (f.comp === rim.comp) continue; // its own interior, not its container
        if (f.area <= rim.area) continue;
        if (!M.pointInPolygon(probe.x, probe.y, f.polygon)) continue;
        if (!best || f.area < best.area) best = f;
      }
      if (best) best.holes.push(rim.polygon);
    }

    const zones = [];
    for (const f of positives) {
      const centroid = M.polygonCentroid(f.polygon);
      const labelPoint = f.holes.length > 0 ? M.polygonVisualCenter(f.polygon, f.holes) : centroid;

      // Must not be inside a hole (subtract room / cutout). Faces with island holes
      // test the label point instead — an annulus centroid can land in its own hole.
      const probe = f.holes.length > 0 ? labelPoint : centroid;
      let inHole = false;
      if (holePolygons) {
        for (const hole of holePolygons) {
          if (hole && hole.length >= 3 && M.pointInPolygon(probe.x, probe.y, hole)) {
            inHole = true;
            break;
          }
        }
      }
      if (inHole) continue;

      // Net area — the island is not part of the surrounding zone
      let area = f.area;
      for (const h of f.holes) area -= Math.abs(M.signedPolygonArea(h));
      if (area < 0.01) continue;

      zones.push({ polygon: f.polygon, holes: f.holes, centroid, labelPoint, area });
    }

    // If no valid zones found, return whole room as single zone
    if (zones.length === 0) {
      return wholeRoomZone();
    }

    // Sort zones top-left to bottom-right (top-to-bottom primary, left-to-right secondary)
    zones.sort((a, b) => {
      const dy = a.centroid.y - b.centroid.y;
      if (Math.abs(dy) > 0.5) return dy; // Y differs meaningfully — sort top first
      return a.centroid.x - b.centroid.x; // same row — sort left first
    });

    return zones;
  };

  /** Compute exterior zones: enclosed areas outside room polygons, formed by
   *  exterior dividing lines connecting to room walls.
   *  @param {Array} roomPolygons — array of room polygon arrays [{x,y}, ...]
   *  @param {Array} exteriorSegments — [{start:{x,y}, end:{x,y}}, ...] line segments outside rooms
   *  @returns {Array} [{polygon, centroid, area}, ...] */
  M.computeExteriorZones = function(roomPolygons, exteriorSegments) {
    if (!roomPolygons || roomPolygons.length === 0) return [];
    if (!exteriorSegments || exteriorSegments.length === 0) return [];

    // Build bounding box around everything with 2ft padding
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const poly of roomPolygons) {
      for (const pt of poly) {
        if (pt.x < minX) minX = pt.x;
        if (pt.y < minY) minY = pt.y;
        if (pt.x > maxX) maxX = pt.x;
        if (pt.y > maxY) maxY = pt.y;
      }
    }
    for (const seg of exteriorSegments) {
      for (const pt of [seg.start, seg.end]) {
        if (pt.x < minX) minX = pt.x;
        if (pt.y < minY) minY = pt.y;
        if (pt.x > maxX) maxX = pt.x;
        if (pt.y > maxY) maxY = pt.y;
      }
    }
    const pad = 2;
    minX -= pad; minY -= pad; maxX += pad; maxY += pad;
    const bbPoly = [
      { x: minX, y: minY }, { x: maxX, y: minY },
      { x: maxX, y: maxY }, { x: minX, y: maxY }
    ];

    // Combine room wall edges + exterior dividing lines as dividers within the BB
    const allDividers = [];
    for (const poly of roomPolygons) {
      for (let i = 0; i < poly.length; i++) {
        const j = (i + 1) % poly.length;
        allDividers.push({ start: poly[i], end: poly[j] });
      }
    }
    for (const seg of exteriorSegments) {
      allDividers.push(seg);
    }

    // Use DCEL zone computation with BB as outer boundary
    const zones = M.computeRoomZones(bbPoly, allDividers, []);

    // Post-filter: remove faces that touch the BB edge (unbounded exterior)
    // and faces that are inside any room polygon (handled by interior zone system)
    const EPS = 0.15;
    const onBB = (pt) => {
      return Math.abs(pt.x - minX) < EPS || Math.abs(pt.x - maxX) < EPS ||
             Math.abs(pt.y - minY) < EPS || Math.abs(pt.y - maxY) < EPS;
    };

    const filtered = [];
    for (const zone of zones) {
      // Skip zones with any vertex on BB edge
      let touchesBB = false;
      for (const pt of zone.polygon) {
        if (onBB(pt)) { touchesBB = true; break; }
      }
      if (touchesBB) continue;

      // Skip zones whose centroid is inside any room polygon
      let insideRoom = false;
      for (const roomPoly of roomPolygons) {
        if (M.pointInPolygon(zone.centroid.x, zone.centroid.y, roomPoly)) {
          insideRoom = true; break;
        }
      }
      if (insideRoom) continue;

      filtered.push(zone);
    }

    // Sort top-left to bottom-right
    filtered.sort((a, b) => {
      const dy = a.centroid.y - b.centroid.y;
      if (Math.abs(dy) > 0.5) return dy;
      return a.centroid.x - b.centroid.x;
    });

    return filtered;
  };

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — smallest rectangle that contains a shape.
     Used to give zone-filled turf its marking frame: the rectangle's long axis is
     the zone's long axis, so hash lines run the way the eye expects even when the
     zone is a slightly-wonky quadrilateral with bowed ends. */

  /** Convex hull, monotone chain. Returns hull points counter-clockwise. */
  M.convexHull = function(points) {
    const pts = points.slice().sort((a, b) => (a.x - b.x) || (a.y - b.y));
    if (pts.length < 3) return pts;
    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower = [];
    for (const p of pts) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper = [];
    for (let i = pts.length - 1; i >= 0; i--) {
      const p = pts[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }
    lower.pop(); upper.pop();
    return lower.concat(upper);
  };

  /**
   * Minimum-area oriented bounding box of a polygon.
   * Returns { cx, cy, width, depth, rotation } — width is the LONGER side and
   * rotation is that side's angle, so a caller can treat it as a wall-aligned
   * rectangle. Rotating calipers in its simplest form: the optimal box always has
   * a side flush with a hull edge, so test one orientation per edge and keep the
   * smallest. Hull sizes here are a few dozen points, so O(n²) is free.
   */
  M.minAreaRect = function(polygon) {
    if (!polygon || polygon.length < 3) return null;
    const hull = M.convexHull(polygon);
    if (hull.length < 3) return null;
    let best = null;
    for (let i = 0; i < hull.length; i++) {
      const a = hull[i], b = hull[(i + 1) % hull.length];
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      const cos = Math.cos(-ang), sin = Math.sin(-ang);
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of hull) {
        const rx = p.x * cos - p.y * sin;
        const ry = p.x * sin + p.y * cos;
        if (rx < minX) minX = rx; if (rx > maxX) maxX = rx;
        if (ry < minY) minY = ry; if (ry > maxY) maxY = ry;
      }
      const w = maxX - minX, h = maxY - minY;
      const area = w * h;
      if (!best || area < best.area - 1e-9) {
        const mx = (minX + maxX) / 2, my = (minY + maxY) / 2;
        const cb = Math.cos(ang), sb = Math.sin(ang);
        best = { area, ang, w, h, cx: mx * cb - my * sb, cy: mx * sb + my * cb };
      }
    }
    if (!best) return null;
    // Report the longer side as `width` so "lengthwise" markings mean what they say.
    const swap = best.h > best.w;
    return {
      cx: best.cx, cy: best.cy,
      width: swap ? best.h : best.w,
      depth: swap ? best.w : best.h,
      rotation: swap ? best.ang + Math.PI / 2 : best.ang
    };
  };

  return M;
})();

